const ACTIVITY_EDIT_ROUTE_PATTERN = /^\/(?:activities|rituals)\/([^/]+)\/edit\/?$/;
const ACTIVITY_MODEL_ROUTE_PATTERN =
  /^\/(?:activities|rituals)\/([^/]+)\/use-model\/?$/;
const EVENT_MODEL_ROUTE_PATTERN = /^\/events\/template\/([^/]+)\/create\/?$/;
const INVENTORY_EDIT_ROUTE_PATTERN = /^\/inventory\/([^/]+)\/edit\/?$/;

const FINANCIAL_EDIT_ROUTE_PATTERN =
  /^\/financial\/(income|outcome)\/([^/]+)\/edit\/?$/;
const TRANSACTION_CREATE_ROUTE_PATTERN = /^\/transactions\/new\/?$/;
const MONTHLY_FEE_EDIT_ROUTE_PATTERN =
  /^\/financial\/monthly-fee\/([^/]+)\/edit\/?$/;
const BANK_ACCOUNT_CREATE_ROUTE_PATTERN = /^\/financial\/new\/?$/;
const BANK_ACCOUNT_EDIT_ROUTE_PATTERN = /^\/financial\/account\/([^/]+)\/?$/;
const RITUAL_BATH_CREATE_ROUTE_PATTERN = /^\/ritual-baths\/new\/?$/;
const RITUAL_BATH_EDIT_ROUTE_PATTERN = /^\/ritual-baths\/([^/]+)\/edit\/?$/;
const RITUAL_BATH_NON_RECORD_SEGMENTS = new Set(["new", "categories"]);

export type FinancialFlow = "income" | "outcome";

/** Flow and record id when the pathname is an income or outcome edit route. */
export const getFinancialEditTarget = (
  pathname: string,
): { flow: FinancialFlow; id: string } | undefined => {
  const match = FINANCIAL_EDIT_ROUTE_PATTERN.exec(pathname);
  return match ? { flow: match[1] as FinancialFlow, id: match[2] } : undefined;
};

/**
 * "Cancelar edição" on an income or outcome edit screen leaves that screen.
 * Creating a lançamento keeps the review card; other edit screens are unchanged.
 */
export const shouldLeaveFinancialEditOnCancel = (
  pathname: string,
  isFieldPickerOpen: boolean,
) => isFieldPickerOpen && getFinancialEditTarget(pathname) !== undefined;

/** `outcome` and `expense` both open a saída; any other type (including none) is an entrada. */
export const isOutcomeTransactionType = (type: string | null | undefined) =>
  type === "outcome" || type === "expense";

/** Flow of the transaction create route. */
export const getFinancialCreateFlow = (
  pathname: string,
  search = "",
): FinancialFlow | undefined => {
  if (!TRANSACTION_CREATE_ROUTE_PATTERN.test(pathname)) return undefined;
  return isOutcomeTransactionType(new URLSearchParams(search).get("type"))
    ? "outcome"
    : "income";
};

/** Inventory item id when the pathname is the inventory edit route, otherwise undefined. */
export const getInventoryEditTargetId = (pathname: string) =>
  INVENTORY_EDIT_ROUTE_PATTERN.exec(pathname)?.[1];

/** Activity id when the pathname is the activity edit route, otherwise undefined. */
export const getActivityEditTargetId = (pathname: string) =>
  ACTIVITY_EDIT_ROUTE_PATTERN.exec(pathname)?.[1];

/** Activity model id when the pathname is the "use model" route, otherwise undefined. */
export const getActivityModelTargetId = (pathname: string) =>
  ACTIVITY_MODEL_ROUTE_PATTERN.exec(pathname)?.[1];

/** Event model id when the pathname is the "create from model" route, otherwise undefined. */
export const getEventModelTargetId = (pathname: string) =>
  EVENT_MODEL_ROUTE_PATTERN.exec(pathname)?.[1];

/** Monthly fee id when the pathname is the monthly fee edit route, otherwise undefined. */
export const getMonthlyFeeEditTargetId = (pathname: string) =>
  MONTHLY_FEE_EDIT_ROUTE_PATTERN.exec(pathname)?.[1];

export const isBankAccountCreateRoute = (pathname: string) =>
  BANK_ACCOUNT_CREATE_ROUTE_PATTERN.test(pathname);

/** Bank account id when the pathname is the bank account edit route, otherwise undefined. */
export const getBankAccountEditTargetId = (pathname: string) =>
  BANK_ACCOUNT_EDIT_ROUTE_PATTERN.exec(pathname)?.[1];

export const isRitualBathCreateRoute = (pathname: string) =>
  RITUAL_BATH_CREATE_ROUTE_PATTERN.test(pathname);

/** Ritual bath id when the pathname is the ritual bath edit route, otherwise undefined. */
export const getRitualBathEditTargetId = (pathname: string) => {
  const id = RITUAL_BATH_EDIT_ROUTE_PATTERN.exec(pathname)?.[1];
  return id && !RITUAL_BATH_NON_RECORD_SEGMENTS.has(id) ? id : undefined;
};
